/**
 * Signing in without ever leaving the app (INV-8): Google Identity Services'
 * own button on the web, the OS sheets in the native apps (Google on both,
 * Apple on iOS). Each hands an ID token and the raw nonce to an exchange —
 * sign-in or linking — and nothing navigates, so there is no second storage
 * jar for a session to land in.
 */

import { haptics } from "@/lib/haptics";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/hooks/use_auth";
import {
  type Session,
  signInWithAppleIdToken,
  signInWithGoogleCredential,
} from "@/lib/backend";
import { createNonce, loadGoogleIdentity } from "@/lib/google_identity";
import type { SignInProvider } from "@/native/bridge";
import { bridge } from "@/native/impl";

/**
 * Google sign-in, exchanged for a session without ever leaving the app (INV-8):
 * Google's own button on the web, the OS sheet in the native apps. Either way
 * the credential and the raw nonce go to `/api/v1/auth/google/idtoken`, which
 * verifies it against Google's JWKS and resolves it to this app's own account.
 */
export function useGoogleSignIn(onSession: (session: Session) => void): GoogleButton {
  const handler = useRef(onSession);
  handler.current = onSession;
  return useGoogleButton(async (credential, rawNonce) => {
    handler.current(await signInWithGoogleCredential(credential, rawNonce));
  });
}

export type SignInStatus = "loading" | "ready" | "signing-in" | "error";

export interface GoogleButton {
  /** Where Google renders its button — web only; unused when `native`. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  status: SignInStatus;
  error: string | null;
  retry: () => void;
  /** True in the native apps: render your own button, and call `start`. */
  native: boolean;
  /** Open the OS sheet (native only; a no-op on the web, where GIS owns the tap). */
  start: () => void;
}

/**
 * A provider's OS sign-in sheet (`bridge.signIn`), handing the ID token and the
 * raw nonce to `exchange`. The nonce's hash goes into the sheet and so into the
 * token; the raw value goes to the server, which checks the two match — the
 * same contract as the web's GIS flow.
 */
export function useNativeSignIn(
  provider: SignInProvider,
  exchange: (idToken: string, rawNonce: string) => Promise<void>,
): { available: boolean; status: SignInStatus; error: string | null; start: () => void; reset: () => void } {
  const available = bridge.signInProviders.includes(provider);
  const [status, setStatus] = useState<SignInStatus>("ready");
  const [error, setError] = useState<string | null>(null);
  const handler = useRef(exchange);
  handler.current = exchange;

  const start = useCallback(() => {
    if (!available) return;
    setStatus("signing-in");
    setError(null);
    void (async () => {
      try {
        const nonce = await createNonce();
        const idToken = await bridge.signIn(provider, nonce.hashed);
        await handler.current(idToken, nonce.raw);
      } catch (cause: unknown) {
        haptics("error");
        setError(cause instanceof Error ? cause.message : "Sign-in failed. Please try again.");
        setStatus("error");
      }
    })();
  }, [available, provider]);

  const reset = useCallback(() => {
    setStatus("ready");
    setError(null);
  }, []);

  return { available, status, error, start, reset };
}

/**
 * Sign in with Apple — iOS only, beside Google with equal prominence (App
 * Store guideline 4.8). The token goes to `/api/v1/auth/apple/idtoken`; an
 * account it creates raises the one-time new-account notice.
 */
export function useAppleSignIn(onSession: (session: Session) => void): ReturnType<typeof useNativeSignIn> {
  const handler = useRef(onSession);
  handler.current = onSession;
  const notice = useAuth().setNewAccountNotice;
  return useNativeSignIn("apple", async (idToken, rawNonce) => {
    const { session, created } = await signInWithAppleIdToken(idToken, rawNonce);
    handler.current(session);
    if (created) notice(true);
  });
}

/**
 * Google, handing its credential and the raw nonce to `exchange`. Sign-in
 * posts them to `/auth/google/idtoken`; linking (Settings › Account) posts them
 * to `/auth/link/google`. On the web it is Google's own button (GIS); in the
 * native apps it is the OS sheet. Same nonce, same rule either way: the ID
 * token arrives in-process and nothing navigates (INV-8).
 *
 * `exchange` rejecting puts its message on screen with a Try again; resolving
 * leaves the status at `signing-in`, because every caller moves on from there
 * (a new screen, or a re-read that hides the button).
 */
export function useGoogleButton(
  exchange: (credential: string, rawNonce: string) => Promise<void>,
): GoogleButton {
  const containerRef = useRef<HTMLDivElement>(null);
  const nativeSheet = useNativeSignIn("google", exchange);
  const native = nativeSheet.available;
  const [status, setStatus] = useState<SignInStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const handler = useRef(exchange);
  handler.current = exchange;

  useEffect(() => {
    // The native apps never load Google's script: the OS sheet replaces it,
    // and the native CSP would refuse it anyway (`src/native/csp.ts`).
    if (native) return;
    const cancelled = { current: false };
    setStatus("loading");
    setError(null);

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setError("Google sign-in is not configured. VITE_GOOGLE_CLIENT_ID is missing.");
      setStatus("error");
      return;
    }

    void (async () => {
      try {
        const [gis, nonce] = await Promise.all([loadGoogleIdentity(), createNonce()]);
        if (cancelled.current || !containerRef.current) return;

        gis.initialize({
          client_id: clientId,
          nonce: nonce.hashed,
          cancel_on_tap_outside: false,
          callback: (response) => {
            setStatus("signing-in");
            handler.current(response.credential, nonce.raw).catch((cause: unknown) => {
              haptics("error");
              setError(cause instanceof Error ? cause.message : "Sign-in failed. Please try again.");
              setStatus("error");
            });
          },
        });

        gis.renderButton(containerRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "pill",
          logo_alignment: "center",
        });
        setStatus("ready");
      } catch (cause: unknown) {
        if (cancelled.current) return;
        setError(cause instanceof Error ? cause.message : "Could not load Google sign-in.");
        setStatus("error");
      }
    })();

    return () => {
      cancelled.current = true;
    };
  }, [attempt, native]);

  if (native) {
    return {
      containerRef,
      status: nativeSheet.status,
      error: nativeSheet.error,
      retry: nativeSheet.reset,
      native: true,
      start: nativeSheet.start,
    };
  }
  return {
    containerRef,
    status,
    error,
    retry: () => setAttempt((n) => n + 1),
    native: false,
    start: () => undefined,
  };
}
